import React, { useState, useEffect, useCallback } from 'react';
import api from '../../utils/axios';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../components/ui/select';
import { toast } from 'sonner';
import { Loader2, RefreshCw, CheckCircle2, XCircle, Database, Search, User, Clock } from 'lucide-react';

export const SERVICES = {
  padron: { label: 'Padrón', color: 'bg-sky-600' },
  apoc: { label: 'Apócrifos', color: 'bg-rose-600' },
  wscdc: { label: 'Comprobantes', color: 'bg-emerald-600' },
  // alias por si quedaron entradas con el tag técnico del web service
  ws_sr_constancia_inscripcion: { label: 'Padrón', color: 'bg-sky-600' },
  wsapoc: { label: 'Apócrifos', color: 'bg-rose-600' },
};

const TIPO_LABEL = {
  constancia: 'Constancia de inscripción',
  apoc: 'Consulta de apócrifos',
  constatar: 'Constatación de comprobante',
};

const fmtFecha = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('es-AR'); } catch { return iso; }
};

const datoConsultado = (row) => {
  const p = row.params || {};
  if (p.cuit) return p.cuit;
  if (p.cuit_emisor) {
    const parts = [`Emisor ${p.cuit_emisor}`];
    if (p.pto_vta !== undefined && p.cbte_nro !== undefined) parts.push(`Cbte ${p.cbte_tipo || ''}-${p.pto_vta}-${p.cbte_nro}`);
    return parts.join(' · ');
  }
  if (row.cuit) return row.cuit; // entradas antiguas (esquema viejo)
  return '—';
};

const money = (v) => {
  if (v === null || v === undefined || v === '') return '—';
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  return n.toLocaleString('es-AR', { style: 'currency', currency: 'ARS', minimumFractionDigits: 2 });
};

const KV = ({ label, value }) => (
  <div className="flex justify-between gap-4 py-1.5 border-b border-slate-100 text-sm">
    <span className="text-slate-500">{label}</span>
    <span className="font-medium text-slate-800 text-right break-all">{value ?? '—'}</span>
  </div>
);

const DetalleContenido = ({ row }) => {
  if (!row) return null;
  const d = row.detalle || {};
  const svc = row.service;

  if (!row.ok) {
    return (
      <div className="p-3 rounded-lg bg-rose-50 border border-rose-200 text-sm text-rose-800">
        <p className="font-medium mb-1">La consulta falló</p>
        <p>{row.error || 'Error desconocido'}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {svc === 'padron' && (
        <div>
          <KV label="Razón social" value={d.razon_social} />
          <KV label="Tipo de persona" value={d.tipo_persona} />
          <KV label="Estado" value={d.estado} />
          <KV label="Condición IVA" value={d.condicion_iva} />
          {d.domicilio && <KV label="Domicilio" value={[d.domicilio.direccion, d.domicilio.localidad, d.domicilio.provincia].filter(Boolean).join(', ')} />}
        </div>
      )}

      {svc === 'apoc' && (
        <div>
          <div className={`p-3 rounded-lg text-sm font-semibold ${d.es_apocrifo ? 'bg-rose-50 text-rose-800 border border-rose-200' : 'bg-emerald-50 text-emerald-800 border border-emerald-200'}`}>
            {d.es_apocrifo ? '⚠ El CUIT figura como APÓCRIFO en la base APOC' : '✓ El CUIT NO figura en la base de apócrifos'}
          </div>
          {Array.isArray(d.publicaciones) && d.publicaciones.map((p, i) => (
            <div key={i} className="mt-2 text-sm border rounded-lg p-3">
              <KV label="CUIT" value={p.cuit} />
              <KV label="Descripción" value={p.descripcion} />
              <KV label="Fecha condición" value={fmtFecha(p.fecha_condicion)} />
              <KV label="Fecha publicación" value={fmtFecha(p.fecha_publicacion)} />
            </div>
          ))}
        </div>
      )}

      {svc === 'wscdc' && (
        <div>
          <div className={`p-3 rounded-lg text-sm font-semibold ${String(d.resultado).toUpperCase() === 'A' ? 'bg-emerald-50 text-emerald-800 border border-emerald-200' : 'bg-rose-50 text-rose-800 border border-rose-200'}`}>
            {String(d.resultado).toUpperCase() === 'A' ? '✓ Comprobante AUTORIZADO' : `Comprobante ${d.resultado === 'R' ? 'RECHAZADO' : (d.resultado || 'sin resultado')}`}
          </div>
          <div className="mt-2">
            <KV label="Fecha de proceso" value={d.fch_proceso} />
            {d.comprobante && <KV label="CUIT emisor" value={d.comprobante.cuit_emisor} />}
            {d.comprobante && <KV label="Importe total" value={money(d.comprobante.imp_total)} />}
            {d.comprobante && <KV label="CAE" value={d.comprobante.cod_autorizacion} />}
          </div>
          {Array.isArray(d.observaciones) && d.observaciones.length > 0 && (
            <div className="mt-2 text-sm">
              <p className="font-medium text-amber-700">Observaciones</p>
              <ul className="list-disc pl-5 text-slate-600">{d.observaciones.map((o, i) => <li key={i}>[{o.code}] {o.msg}</li>)}</ul>
            </div>
          )}
          {Array.isArray(d.errores) && d.errores.length > 0 && (
            <div className="mt-2 text-sm">
              <p className="font-medium text-rose-700">Errores</p>
              <ul className="list-disc pl-5 text-rose-600">{d.errores.map((o, i) => <li key={i}>[{o.code}] {o.msg}</li>)}</ul>
            </div>
          )}
        </div>
      )}

      <details>
        <summary className="text-xs text-slate-500 cursor-pointer">Ver JSON completo</summary>
        <pre className="mt-2 max-h-72 overflow-auto text-xs bg-slate-900 text-slate-100 p-3 rounded-lg">
          {JSON.stringify(row.detalle ?? row, null, 2)}
        </pre>
      </details>
    </div>
  );
};

export const HistorialPanel = ({ service = null, showFilter = false, limit = 100 }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtro, setFiltro] = useState(service || 'all');
  const [detalle, setDetalle] = useState(null);
  const [loadingDetalle, setLoadingDetalle] = useState(false);

  const svcParam = service || (filtro !== 'all' ? filtro : null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit };
      if (svcParam) params.service = svcParam;
      const res = await api.get('/arca/historial', { params });
      setRows(res.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error cargando el historial');
    } finally {
      setLoading(false);
    }
  }, [svcParam, limit]);

  useEffect(() => { load(); }, [load]);

  const abrirDetalle = async (row) => {
    setDetalle(row);
    setLoadingDetalle(true);
    try {
      const res = await api.get(`/arca/historial/${row.id}`);
      setDetalle(res.data);
    } catch {
      // dejamos la fila básica
    } finally {
      setLoadingDetalle(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="arca-historial-panel">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          {showFilter && (
            <Select value={filtro} onValueChange={setFiltro}>
              <SelectTrigger className="w-48" data-testid="arca-historial-filtro">
                <SelectValue placeholder="Servicio" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los servicios</SelectItem>
                <SelectItem value="padron">Padrón</SelectItem>
                <SelectItem value="apoc">Apócrifos</SelectItem>
                <SelectItem value="wscdc">Comprobantes (WSCDC)</SelectItem>
              </SelectContent>
            </Select>
          )}
          <span className="text-sm text-slate-500">{rows.length} consulta(s)</span>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="arca-historial-refresh">
          {loading ? <Loader2 size={15} className="mr-1 animate-spin" /> : <RefreshCw size={15} className="mr-1" />} Actualizar
        </Button>
      </div>

      <Card className="p-0 overflow-hidden">
        {loading ? (
          <div className="p-8 flex justify-center"><Loader2 className="animate-spin text-slate-400" /></div>
        ) : rows.length === 0 ? (
          <div className="p-8 text-center text-slate-400 text-sm">Todavía no se registraron consultas.</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Fecha/hora</TableHead>
                {showFilter && <TableHead>Servicio</TableHead>}
                <TableHead>Tipo</TableHead>
                <TableHead>Dato consultado</TableHead>
                <TableHead>Usuario</TableHead>
                <TableHead>Origen</TableHead>
                <TableHead>Resultado</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row, idx) => {
                const svc = SERVICES[row.service] || { label: row.service || 'Padrón', color: 'bg-slate-500' };
                const clickable = !!row.id;
                return (
                  <TableRow
                    key={row.id || idx}
                    className={clickable ? 'cursor-pointer' : ''}
                    onClick={() => clickable && abrirDetalle(row)}
                    data-testid={`arca-historial-row-${row.id || idx}`}
                  >
                    <TableCell className="whitespace-nowrap text-slate-600"><Clock size={12} className="inline mr-1 text-slate-400" />{fmtFecha(row.at)}</TableCell>
                    {showFilter && <TableCell><Badge className={svc.color}>{svc.label}</Badge></TableCell>}
                    <TableCell className="text-slate-600">{TIPO_LABEL[row.tipo] || row.tipo}</TableCell>
                    <TableCell className="font-mono text-slate-800">{datoConsultado(row)}</TableCell>
                    <TableCell className="text-slate-600"><User size={12} className="inline mr-1 text-slate-400" />{row.usuario || (row.source === 'external' ? 'API externa' : '—')}</TableCell>
                    <TableCell>
                      <span className="text-xs text-slate-500">{row.source === 'external' ? 'Externo' : 'Interno'}</span>
                      {row.cached && <Badge variant="outline" className="ml-1 text-slate-500"><Database size={11} className="mr-1" />caché</Badge>}
                    </TableCell>
                    <TableCell>
                      {row.ok ? (
                        <span className="inline-flex items-center gap-1 text-emerald-600 text-sm"><CheckCircle2 size={15} /> OK</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-rose-600 text-sm" title={row.error}><XCircle size={15} /> Error</span>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Card>

      <Dialog open={!!detalle} onOpenChange={(o) => !o && setDetalle(null)}>
        <DialogContent className="max-w-xl" data-testid="arca-historial-detalle">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Search size={16} /> Detalle de la consulta
            </DialogTitle>
          </DialogHeader>
          {detalle && (
            <div className="space-y-3">
              <div className="flex flex-wrap gap-2 items-center text-sm">
                <Badge className={(SERVICES[detalle.service] || {}).color || 'bg-slate-500'}>{(SERVICES[detalle.service] || {}).label || detalle.service}</Badge>
                <span className="text-slate-500">{fmtFecha(detalle.at)}</span>
                <span className="text-slate-400">·</span>
                <span className="text-slate-500">{detalle.usuario || (detalle.source === 'external' ? 'API externa' : '—')}</span>
                {detalle.cached && <Badge variant="outline" className="text-slate-500">caché</Badge>}
              </div>
              <KV label="Dato consultado" value={datoConsultado(detalle)} />
              {loadingDetalle ? <div className="py-4 flex justify-center"><Loader2 className="animate-spin text-slate-400" /></div> : <DetalleContenido row={detalle} />}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HistorialPanel;
